/**
 * VERIX — ConsentManager
 * Sprint 15 | RGPD Art. 6, 7, 13
 */

import { useState, useEffect, useCallback } from 'react';
import { Shield, ChevronDown, ChevronUp, Info, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '../../utils/api';
import { safeStorage } from '@/lib/safeStorage';
import { formatDateShort } from '@/lib/utils';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const CONSENT_VERSION = '1.0';
const CONSENT_KEY = 'verix_consent_v1';
const CONSENT_SHOWN_KEY = 'verix_consent_shown';

// ── Hook useConsent ───────────────────────────────────────────────────────────

export function useConsent() {
  const [consent, setConsent] = useState(() => {
    try {
      const stored = safeStorage.getItem(CONSENT_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, isLoadingAuth } = useAuth();

  useEffect(() => {
    if (isLoadingAuth || !isAuthenticated) return;
    apiFetch('/api/consent')
      .then((data) => {
        if (data && data.consent) {
          setConsent(data.consent);
          safeStorage.setItem(CONSENT_KEY, JSON.stringify(data.consent));
        }
      })
      .catch(() => {});
  }, [isAuthenticated, isLoadingAuth]);

  /**
   * SIN SESION NO SE LLAMA AL SERVIDOR.
   *
   * El banner se monta en `App.jsx`, o sea tambien en el portal publico de factura
   * (`/portal/invoice/:token`), que lo abre el DESTINATARIO desde el enlace del
   * email y no tiene cuenta. Ahi `POST /api/consent` responde 401, y el cliente
   * reacciona al 401 como a una sesion caducada: intenta refrescar, no puede,
   * llama a `/api/auth/logout` y manda a `/login` (`api/apiFetch.ts`).
   *
   *   401 POST /api/v1/consent -> 401 POST /api/auth/refresh
   *   -> 200 POST /api/auth/logout -> /login
   *
   * Resultado: el cliente aceptaba las cookies y acababa en la pantalla de acceso
   * de un ERP que no es suyo, perdiendo la factura que estaba leyendo.
   *
   * El GET de hidratacion ya se saltaba sin sesion; esto es la otra mitad. El
   * consentimiento de un visitante anonimo vive solo en su navegador, que es donde
   * viven las cookies a las que se refiere.
   */
  const grantConsent = useCallback(async ({ analytics, marketing }) => {
    setLoading(true);
    const guardar = (c) => {
      setConsent(c);
      safeStorage.setItem(CONSENT_KEY, JSON.stringify(c));
      safeStorage.setItem(CONSENT_SHOWN_KEY, 'true');
    };
    const local = { functional: true, analytics, marketing, version: CONSENT_VERSION, granted_at: new Date().toISOString() };
    try {
      if (!isAuthenticated) {
        guardar(local);
        return;
      }
      const payload = { functional: true, analytics, marketing, version: CONSENT_VERSION };
      const data = await apiFetch('/api/consent', { method: 'POST', body: JSON.stringify(payload) });
      guardar(data.consent);
    } catch {
      guardar(local);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Retirada del consentimiento (Art. 7.3). Devuelve `true` solo si el servidor
   * la confirma.
   *
   * Antes el fallo del DELETE se tragaba en silencio y la revocación se escribía
   * igual en local. Eso revertía la retirada sin que nadie se enterara: el
   * servidor conservaba `analytics: true`, y la hidratación del siguiente arranque
   * (el GET de arriba) sobrescribía el local con el consentimiento viejo →
   * analytics reactivado. Para un botón que se anuncia como Art. 7.3, eso es
   * justo lo que el artículo prohíbe.
   */
  const revokeConsent = useCallback(async () => {
    setLoading(true);
    try {
      await apiFetch('/api/consent', { method: 'DELETE' });
      const revoked = consent
        ? { ...consent, analytics: false, marketing: false, revoked_at: new Date().toISOString() }
        : null;
      setConsent(revoked);
      if (revoked) safeStorage.setItem(CONSENT_KEY, JSON.stringify(revoked));
      // Y se olvida que ya se enseño el banner: si no, `hasShown` sigue en true de
      // por vida en este navegador y la retirada no se puede reconsiderar nunca.
      safeStorage.removeItem(CONSENT_SHOWN_KEY);
      return true;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  }, [consent]);

  return {
    consent,
    grantConsent,
    revokeConsent,
    loading,
    // «Ha consentido» NO es «tenemos un registro suyo»: un consentimiento RETIRADO
    // es un registro que existe y que dice justo lo contrario. Con `consent !== null`
    // el banner no volvia a salir NUNCA despues de una retirada — el servidor
    // respondia `has_consented: false` y la app lo ignoraba, dejando al usuario en
    // un limbo donde ni ha consentido ni se le pregunta. Misma regla que el backend
    // (`routes/shared/consent.ts`: `has_consented: !consent.revoked_at`).
    hasConsented: consent !== null && !consent.revoked_at,
    hasShown: safeStorage.getItem(CONSENT_SHOWN_KEY) === 'true',
  };
}

// ── Categorías ────────────────────────────────────────────────────────────────

const CATEGORIES = [
  {
    id: 'functional',
    title: 'Cookies funcionales',
    description: 'Estrictamente necesarias para el funcionamiento de la plataforma.',
    details: ['Sesión de autenticación (JWT/Cognito)', 'Token CSRF', 'Preferencias de idioma y tema'],
    basis: 'Base legal: ejecución del contrato (Art. 6.1.b RGPD)',
    required: true,
    color: 'green',
  },
  {
    id: 'analytics',
    title: 'Cookies de análisis',
    description: 'Nos ayudan a entender cómo usas VERIX para mejorar la experiencia.',
    details: ['Páginas más visitadas', 'Tiempo de respuesta', 'Errores técnicos (CloudWatch)'],
    basis: 'Base legal: consentimiento (Art. 6.1.a RGPD)',
    required: false,
    color: 'blue',
  },
  {
    id: 'marketing',
    title: 'Cookies de comunicación',
    description: 'Permiten enviarte información sobre nuevas funcionalidades de VERIX.',
    details: ['Notificaciones de nuevas funcionalidades', 'Actualizaciones del producto'],
    basis: 'Base legal: consentimiento (Art. 6.1.a RGPD)',
    required: false,
    color: 'purple',
  },
];

// ── CategoryCard ──────────────────────────────────────────────────────────────

function CategoryCard({ category, enabled, onToggle }) {
  const [expanded, setExpanded] = useState(false);

  const colorMap = {
    green: 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800',
    blue: 'bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800',
    purple: 'bg-purple-50 border-purple-200 dark:bg-purple-950 dark:border-purple-800',
  };
  const badgeMap = {
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
  };

  return (
    <div className={`rounded-lg border p-4 ${colorMap[category.color]}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm">{category.title}</span>
            {category.required && (
              <Badge className={`text-xs ${badgeMap[category.color]}`}>Obligatorio</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{category.description}</p>
        </div>
        <Switch checked={enabled} onCheckedChange={onToggle} disabled={category.required} />
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 mt-2 text-xs text-muted-foreground hover:text-muted-foreground"
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? 'Ocultar detalles' : 'Ver detalles'}
      </button>
      {expanded && (
        <div className="mt-3 space-y-1">
          {category.details.map((d, i) => (
            <p key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-slate-400 shrink-0" />
              {d}
            </p>
          ))}
          <p className="text-xs text-muted-foreground italic mt-2">{category.basis}</p>
        </div>
      )}
    </div>
  );
}

// ── ConsentManager (modal) ────────────────────────────────────────────────────

export default function ConsentManager() {
  const { t } = useTranslation();
  const { grantConsent, loading, hasConsented, hasShown } = useConsent();
  const [open, setOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!hasShown && !hasConsented) {
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [hasConsented, hasShown]);

  const handleAcceptAll = async () => {
    await grantConsent({ analytics: true, marketing: true });
    setOpen(false);
    toast.success(t('consent.preferences_saved'));
  };

  const handleAcceptSelected = async () => {
    await grantConsent({ analytics, marketing });
    setOpen(false);
    toast.success(t('consent.preferences_saved'));
  };

  const handleRejectAll = async () => {
    await grantConsent({ analytics: false, marketing: false });
    setOpen(false);
    toast.success(t('consent.only_functional'));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle>Tu privacidad en VERIX</DialogTitle>
              <p className="text-sm text-muted-foreground mt-0.5">THEPINEAPPLE CORPORATION S.L. · dpo@verix.es</p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <p className="text-sm text-muted-foreground">
            Utilizamos cookies propias para ofrecerte el servicio ERP. Puedes personalizar tu elección o rechazar las opcionales.
            Tu consentimiento es revocable en cualquier momento desde <strong>Configuración → Privacidad</strong>.
          </p>

          <div className="space-y-3">
            {CATEGORIES.map((cat) => (
              <CategoryCard
                key={cat.id}
                category={cat}
                enabled={cat.id === 'functional' ? true : cat.id === 'analytics' ? analytics : marketing}
                onToggle={cat.id === 'analytics' ? setAnalytics : cat.id === 'marketing' ? setMarketing : undefined}
              />
            ))}
          </div>

          <div className="flex items-start gap-2 p-3 bg-muted rounded-lg border text-xs text-muted-foreground">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              Responsable: THEPINEAPPLE CORPORATION S.L. Derechos: acceso, rectificación, supresión, oposición, portabilidad
              contactando a dpo@verix.es.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" size="sm" onClick={handleRejectAll} disabled={loading} className="flex-1">
              Solo funcionales
            </Button>
            <Button variant="outline" size="sm" onClick={handleAcceptSelected} disabled={loading} className="flex-1">
              <Check className="w-4 h-4 mr-1" />Guardar selección
            </Button>
            <Button size="sm" onClick={handleAcceptAll} disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700">
              Aceptar todas
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── ConsentSettingsPanel ──────────────────────────────────────────────────────

export function ConsentSettingsPanel() {
  const { t } = useTranslation();
  const { consent, grantConsent, revokeConsent, loading } = useConsent();
  const [analytics, setAnalytics] = useState(consent ? consent.analytics : false);
  const [marketing, setMarketing] = useState(consent ? consent.marketing : false);
  const revoked = Boolean(consent?.revoked_at);

  useEffect(() => {
    if (consent) {
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
    }
  }, [consent]);

  const handleSave = async () => {
    await grantConsent({ analytics, marketing });
    toast.success('Preferencias de privacidad guardadas');
  };

  // Solo se anuncia la retirada si el servidor la confirmó: decirle al usuario
  // "revocado" mientras el backend conserva el consentimiento sería exactamente
  // lo que el Art. 7.3 prohíbe.
  const handleRevoke = async () => {
    const ok = await revokeConsent();
    if (ok) toast.success('Consentimiento retirado');
    else toast.error(t('consent.revoke_error'));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          Privacidad y cookies
        </CardTitle>
        <CardDescription>
          Decide qué datos puede tratar VERIX. Puedes cambiarlo o retirarlo cuando quieras.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg border border-border">
          <Shield className="w-5 h-5 text-primary shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">Estado del consentimiento</p>
            <p className="text-xs text-muted-foreground">
              {consent
                ? `Registrado el ${formatDateShort(consent.granted_at)} · v${consent.version}`
                : 'No has registrado tus preferencias aún'}
            </p>
          </div>
          <Badge
            className={`ml-auto border-0 ${
              revoked
                ? 'bg-muted text-muted-foreground'
                : consent
                  ? 'bg-success/10 text-success'
                  : 'bg-warning/10 text-warning'
            }`}
          >
            {revoked ? 'Retirado' : consent ? 'Registrado' : 'Pendiente'}
          </Badge>
        </div>

        <div className="space-y-3">
          {CATEGORIES.map((cat) => (
            <div key={cat.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div>
                <p className="text-sm font-medium text-foreground">{cat.title}</p>
                <p className="text-xs text-muted-foreground">{cat.description}</p>
              </div>
              <Switch
                checked={cat.id === 'functional' ? true : cat.id === 'analytics' ? analytics : marketing}
                onCheckedChange={cat.id === 'analytics' ? setAnalytics : cat.id === 'marketing' ? setMarketing : undefined}
                disabled={cat.required || loading}
              />
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={handleSave} disabled={loading}>
            Guardar preferencias
          </Button>
          <Button variant="outline" onClick={handleRevoke} disabled={loading || !consent || revoked} className="text-destructive border-destructive/30">
            Retirar consentimiento (Art. 7.3)
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
